import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { List, ListItem, Page, Searchbar } from 'framework7-react';

import { I18n } from 'Locales';
import { mockFunction } from 'Helpers';
import { NavigationService } from 'Services';
import { ColumnView, RowView } from 'Containers';
import { AppHeader, Button, Fab } from 'Components';

import './style.scss';
import { filters, yesterday } from '../utils/FiltersConfig';

const groupByCategory = filtersType => filters[filtersType] || {};

const navigateBack = ({ history = '', setSelectedFilters = mockFunction }) => {
  NavigationService.goBack({ name: history[history.length - 1] });
  setSelectedFilters({});
};

const saveFilters = ({
  adminFilters = {},
  campaignType = '',
  filtersType = '',
  history = [],
  navigateBack = mockFunction,
  selectedFilters = {},
  setSelectedFilters = mockFunction,
  updateTransientProps = mockFunction,
  updateUser = mockFunction,
  updateUserProps = mockFunction,
  userAdminFilters = {},
  view = ''
}) => {
  const campaignKey = campaignType.toLowerCase();
  const viewKey = view.toLowerCase();

  const previousCampaignFilters = userAdminFilters?.[campaignKey] || {};
  const previousViewFilters = previousCampaignFilters?.[viewKey] || {};

  const newFilterData = campaignType
    ? {
        [campaignKey]: {
          ...previousCampaignFilters,
          [viewKey]: {
            ...previousViewFilters,
            [filtersType]: selectedFilters[filtersType]
          }
        }
      }
    : { [filtersType]: selectedFilters[filtersType] };

  updateUserProps({
    adminFilters: {
      ...adminFilters,
      [filtersType]: selectedFilters[filtersType]
    }
  });

  const prevDateArr = previousViewFilters?.dailyFigures?.date || [];
  const currDateArr = selectedFilters?.[filtersType]?.date || [];

  if (prevDateArr.length === 0 && currDateArr.length > 0) {
    // Reset the date filters to yesterday if they were not set before
    updateTransientProps({
      filters: {
        dailyFiguresDate_end_date: yesterday,
        dailyFiguresDate_start_date: yesterday
      }
    });
  }

  const userFilterPayload = {
    data: {
      attributes: {
        admin_filters: {
          ...userAdminFilters,
          ...newFilterData
        }
      }
    }
  };

  updateUser(userFilterPayload);
  navigateBack({ history, setSelectedFilters });
};

const handleCheckboxChange = ({
  category = '',
  filter = {},
  filtersType = '',
  selectedFilters = {},
  setSelectedFilters = mockFunction
}) => {
  const newSelectedFilters = JSON.parse(JSON.stringify(selectedFilters));
  if (!newSelectedFilters[filtersType]) {
    newSelectedFilters[filtersType] = {};
  }
  if (!newSelectedFilters[filtersType][category]) {
    newSelectedFilters[filtersType][category] = [];
  }
  const isSelected = newSelectedFilters[filtersType][category].some(
    selectedFilter => selectedFilter?.name === filter?.name
  );
  if (isSelected) {
    newSelectedFilters[filtersType][category] = newSelectedFilters[filtersType][category].filter(
      selectedFilter => selectedFilter?.name !== filter?.name
    );
  } else {
    newSelectedFilters[filtersType][category].push(filter);
  }

  setSelectedFilters(newSelectedFilters);
};

const AdvancedFilters = ({
  adminFilters = {},
  campaignType = '',
  filtersType = '',
  history = [],
  role = '',
  updateTransientProps = mockFunction,
  updateUser = mockFunction,
  updateUserProps = mockFunction,
  userAdminFilters = {},
  view = ''
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const groupedFilters = groupByCategory(filtersType);

  const getInitialSelectedFilters = () => {
    if (campaignType !== '' && view !== '') {
      const viewKey = view.toLowerCase();
      const campaignKey = campaignType.toLowerCase();

      const userFilters = userAdminFilters?.[campaignKey]?.[viewKey]?.[filtersType];
      const hasUserFilters = userFilters && Object.keys(userFilters).length > 0;

      if (hasUserFilters) {
        return userAdminFilters[campaignKey][viewKey];
      }
    } else {
      const userFilters = userAdminFilters?.[filtersType];
      const hasUserFilters = userFilters && Object.keys(userFilters).length > 0;

      if (hasUserFilters) {
        return userAdminFilters;
      }
    }

    return adminFilters;
  };

  const [selectedFilters, setSelectedFilters] = useState(getInitialSelectedFilters);

  const filteredGroupedFilters = Object.keys(groupedFilters)?.reduce((acc, category) => {
    const filteredFilters = groupedFilters[category]?.filter(filter => {
      const isVisibleForRole = !filter.displayForRole || filter.displayForRole.includes(role);
      const isVisibleForView = !filter.displayInView || filter.displayInView.includes(view);
      const matchesSearch = I18n.t(`filters:${filtersType}.${filter?.name}`)
        ?.toLowerCase()
        ?.includes(searchQuery?.toLowerCase());

      return isVisibleForRole && isVisibleForView && matchesSearch;
    });

    if (filteredFilters?.length > 0) {
      acc[category] = filteredFilters;
    }
    return acc;
  }, {});

  return (
    <Page id="filter-type">
      <AppHeader
        user
        goBack
        hideAvatar
        title={I18n.t('general:filterType')}
        handleClick={navigateBack.bind(null, { history, setSelectedFilters })}
      />

      <RowView paddingHorizontal={10} marginTop={30}>
        <Searchbar
          disableButton={false}
          clearButton={false}
          placeholder={I18n.t('general:searchFilter')}
          value={searchQuery || ''}
          onInput={e => setSearchQuery(e.target.value)}
        />
      </RowView>

      {!Object.values(filteredGroupedFilters)?.length ? (
        <RowView paddingVertical={12}>{I18n.t('general:noResultsFound')}</RowView>
      ) : (
        <ColumnView paddingHorizontal={21} paddingBottom={100}>
          {Object.keys(filteredGroupedFilters)?.map(category => (
            <ColumnView key={category}>
              <RowView justifyContent={'flex-start'} paddingTop={15}>
                <div className="category">{I18n.t(`filters:categories.${category}`)}</div>
              </RowView>
              {filteredGroupedFilters[category]?.map(filter => (
                <List key={filter?.name} className="w-full">
                  <ListItem
                    checked={
                      !!selectedFilters[filtersType]?.[category]?.some(
                        selectedFilter => selectedFilter?.name === filter?.name
                      )
                    }
                    checkbox={true}
                    onClick={handleCheckboxChange.bind(null, {
                      category,
                      filter,
                      filtersType,
                      selectedFilters,
                      setSelectedFilters
                    })}>
                    {I18n.t(`filters:${filtersType}.${filter?.name}`)}
                  </ListItem>
                </List>
              ))}
            </ColumnView>
          ))}
        </ColumnView>
      )}

      <Fab className={'w-full px-20'} position={'center-bottom'} slot={'fixed'}>
        <Button.Primary
          width={'100%'}
          onClick={saveFilters.bind(null, {
            adminFilters,
            campaignType,
            filtersType,
            history,
            navigateBack,
            selectedFilters,
            setSelectedFilters,
            updateTransientProps,
            updateUser,
            updateUserProps,
            userAdminFilters,
            view
          })}>
          {I18n.t('general:save')}
        </Button.Primary>
      </Fab>
    </Page>
  );
};

AdvancedFilters.propTypes = {
  adminFilters: PropTypes.object,
  campaignType: PropTypes.string,
  filtersType: PropTypes.string,
  history: PropTypes.array,
  role: PropTypes.string,
  updateTransientProps: PropTypes.func,
  updateUser: PropTypes.func,
  updateUserProps: PropTypes.func,
  userAdminFilters: PropTypes.object,
  view: PropTypes.string
};

export default AdvancedFilters;
